import React, { useState, useEffect } from 'react';

const SpaceForm = ({ propertyId, initialData, occupancyAuthority = null, onSubmit, onCancel }) => {
  const [occupancyWarning, setOccupancyWarning] = useState('');
  const [formData, setFormData] = useState({
    propertyId: propertyId, name: '', spaceType: 'Unit', furnished: 'Unfurnished',
    status: 'Available', weeklyRent: 0, notes: '', expenseAllocationFlag: 'Standard'
  });

  useEffect(() => {
    if (initialData) setFormData({ ...formData, ...initialData });
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    if (name === 'status') {
      const occupiedByLease = Boolean(occupancyAuthority?.occupied || initialData?.currentLeaseId);
      if (occupiedByLease && value !== 'Occupied') {
        setOccupancyWarning('Occupancy is automatic. An effective lease is attached to this space, so it must remain Occupied until that lease ends or is terminated.');
        return;
      }
      if (!occupiedByLease && value === 'Occupied') {
        setOccupancyWarning('Occupancy is automatic. Create or activate a lease for this space to mark it Occupied.');
        return;
      }
      setOccupancyWarning('');
    }
    setFormData(prev => ({ ...prev, [name]: type === 'number' ? Number(value) : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const occupiedByLease = Boolean(occupancyAuthority?.occupied || initialData?.currentLeaseId);
    onSubmit({ ...formData, status: occupiedByLease ? 'Occupied' : formData.status === 'Occupied' ? 'Available' : formData.status });
  };

  const inputClass = "w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white";
  const labelClass = "block text-xs font-medium text-slate-600 mb-1";

  const renderField = (name, label, type = 'text', options = null) => (
    <div>
      <label className={labelClass}>{label}</label>
      {options ? (
        <select name={name} value={formData[name]} onChange={handleChange} className={inputClass}>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input type={type} name={name} value={formData[name] || ''} onChange={handleChange} className={inputClass} step={type === 'number' ? '0.01' : undefined} />
      )}
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          {renderField('name', 'Space Name / Number (e.g., Unit 1, Room A)', 'text')}
        </div>
        {renderField('spaceType', 'Space Type', 'text', ['Unit', 'House', 'Room', 'Office', 'Shop', 'Warehouse', 'Parking', 'Other'])}
        <div>
          {renderField('status', 'Occupancy Status', 'text', ['Available', 'Occupied', 'Under Maintenance', 'Archived'])}
          <p className="mt-1 text-[11px] text-slate-400">Automatic from the effective lease. Manual changes cannot override an active tenancy.</p>
          {occupancyWarning && <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">{occupancyWarning}</div>}
        </div>
        {renderField('weeklyRent', 'Expected Weekly Rent ($)', 'number')}
        {renderField('furnished', 'Furnished Status', 'text', ['Unfurnished', 'Partially Furnished', 'Fully Furnished'])}
        {renderField('expenseAllocationFlag', 'Expense Allocation', 'text', ['Standard', 'Full Recovery', 'None'])}
        <div className="md:col-span-2">
          <label className={labelClass}>Notes</label>
          <textarea name="notes" value={formData.notes || ''} onChange={handleChange} className={inputClass} rows="3" />
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
        <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg">Save Space</button>
      </div>
    </form>
  );
};

export default SpaceForm;